import FrameController from "@/core/FrameController";
import {
  Config,
  EventType,
  InitMessage,
  isOfferReadyMessage,
  isOfferStateChangeMessage,
  isReadyMessage,
  isResizeMessage,
  OfferReadyEvent,
  OfferStateChangeEvent,
  ErrorEvent,
  ProductConfig,
  ComponentConfig,
  TokenizePaymentMessage,
  EventInsuranceRequirementsStateChangeEvent,
  isEventInsuranceRequirementsStateChangeEvent,
  isEventExhibitorLiabilityStateChangeMessage,
  EventExhibitorLiabilityStateChangeEvent,
  uuidv4,
  isStateChangeMessage,
  StateChangeEvent,
  isErrorMessage
} from "@vertical-insure/shared";
import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';

const EMBEDDED_APP_URL = import.meta.env.VITE_EMBEDDED_APP_URL || 'https://embedded.verticalinsure.com';
const version = __APP_VERSION__;
const VERTICAL_INSURE_API_URL = import.meta.env.VITE_VERTICAL_INSURE_API_URL || 'https://api.verticalinsure.com';

type ChangeHandler = (event: StateChangeEvent) => void

export class VerticalInsure {

  /* @internal */
  private element: HTMLIFrameElement;

  /* @internal */
  private config: Config;

  /* @internal */
  private wrapper: HTMLElement;

  /* @internal */
  private frameController: FrameController;

  /* @internal */
  private frameId: string;

  /* @internal */
  private frameOrigin: string = EMBEDDED_APP_URL;

  /* @internal */
  private frameSource: string;

  /* @internal */
  onOfferStateChange?: (offerState: OfferStateChangeEvent) => void;

  /* @internal */
  onOfferReady?: (offersAvailable: boolean) => void;

  /* @internal */
  onEventInsuranceRequirementsStateChange?: (eventConfig: EventInsuranceRequirementsStateChangeEvent) => void;

  /* @internal */
  onEventExhibitorLiabilityStateChange?: (eventConfig: EventExhibitorLiabilityStateChangeEvent) => void;

  /* @internal */
  onChange?: ChangeHandler;

  /* @internal */
  onError?: (error: ErrorEvent) => void;

  /* @internal */
  private eventAbort = new AbortController();

  /**
   * Initialize the Vertical Insure offer.
   * @param elementSelector - The selector of the element to mount the offer to.
   * @param config - The configuration object for the offer.
   * @param onOfferStateChange - A callback function to be called when the offer state changes.
   * @param onOfferReady - A callback function to be called when the offer is ready. Receives a boolean indicating if offers are available.
   */
  constructor(elementSelector: string, config: Config, onOfferStateChange?: (offerState: OfferStateChangeEvent) => void, onOfferReady?: (offersAvailable: boolean) => void);
  /**
   * Initialize the Vertical Insure offer.
   * @param elementSelector - The selector of the element to mount the offer to.
   * @param config - The configuration object for the offer.
   * @param eventHandlers - An object containing optional event handler callbacks.
   */
  constructor(elementSelector: string, config: Config, eventHandlers: {
    onOfferStateChange?: (offerState: OfferStateChangeEvent) => void,
    onOfferReady?: (offersAvailable: boolean) => void,
    /**
     * @deprecated Use `onChange`
     */
    onEventInsuranceRequirementsStateChange?: (eventConfig: EventInsuranceRequirementsStateChangeEvent) => void,

    /**
     * @deprecated Use `onChange`
     */
    onEventExhibitorLiabilityStateChange?: (eventConfig: EventExhibitorLiabilityStateChangeEvent) => void,
    onChange?: ChangeHandler,
    onError?: (error: ErrorEvent) => void
  });
  constructor(
    elementSelector: string,
    config: Config,
    onOfferStateChangeOrEventHandlers?: ((offerState: OfferStateChangeEvent) => void) | {

      /**
       * @deprecated Use `onChange`
       */
      onOfferStateChange?: (offerState: OfferStateChangeEvent) => void,
      onOfferReady?: (offersAvailable: boolean) => void,

      /**
       * @deprecated Use `onChange`
       */
      onEventInsuranceRequirementsStateChange?: (eventConfig: EventInsuranceRequirementsStateChangeEvent) => void,

      /**
       * @deprecated Use `onChange`
       */
      onEventExhibitorLiabilityStateChange?: (eventConfig: EventExhibitorLiabilityStateChangeEvent) => void,

      onChange?: ChangeHandler,
      onError?: (error: ErrorEvent) => void
    },
    onOfferReady?: (offersAvailable: boolean) => void
  ) {
    // Validate required fields
    if (!config.client_id) {
      throw new Error('client_id is required in config');
    }

    this.frameId = `vertical-insure-frame-container-${uuidv4()}`;
    this.frameSource = `${EMBEDDED_APP_URL}/v1?frameId=${this.frameId}`;
    this.config = config;

    const wrapper = document.querySelector<HTMLElement>(elementSelector);
    if (wrapper === null) {
      throw Error(`Could not find element with selector: ${elementSelector}`);
    }

    this.wrapper = wrapper;
    this.element = document.createElement("iframe");
    this.element.id = this.frameId;
    this.frameController = new FrameController(this.element, this.frameSource);

    // Handle both calling patterns
    if (typeof onOfferStateChangeOrEventHandlers === 'function') {
      // First pattern: individual parameters
      this.onOfferStateChange = onOfferStateChangeOrEventHandlers;
      this.onOfferReady = onOfferReady;
    } else if (onOfferStateChangeOrEventHandlers && typeof onOfferStateChangeOrEventHandlers === 'object') {
      // Second pattern: eventHandlers object
      this.onOfferStateChange = onOfferStateChangeOrEventHandlers.onOfferStateChange;
      this.onOfferReady = onOfferStateChangeOrEventHandlers.onOfferReady;
      this.onEventInsuranceRequirementsStateChange = onOfferStateChangeOrEventHandlers.onEventInsuranceRequirementsStateChange;
      this.onEventExhibitorLiabilityStateChange = onOfferStateChangeOrEventHandlers.onEventExhibitorLiabilityStateChange;
      this.onChange = onOfferStateChangeOrEventHandlers.onChange;
      this.onError = onOfferStateChangeOrEventHandlers.onError;
    } else if (onOfferStateChangeOrEventHandlers === undefined) {
      // Handle case where onOfferStateChange is undefined but onOfferReady may be provided
      this.onOfferReady = onOfferReady;
    }

    this._init();
  }

  /* @internal */
  private _init() {
    window.addEventListener("message", (event: MessageEvent<unknown>) => {
      if (event.origin === this.frameOrigin && this.element.contentWindow === event.source) {
        if (isReadyMessage(event.data)) {
          const initMessage = {
            eventType: EventType.init,
            ...this.config,
            version: version,
            origin: window.location.origin
          } satisfies InitMessage;

          this.frameController.postMessage(EventType.init, initMessage);
        }

        if (isResizeMessage(event.data)) {
          if (this.element) {
            this.element.width = `${event.data.width}px`;
            this.element.height = `${event.data.height}px`;
          }
        }

        if (isOfferStateChangeMessage(event.data)) {
          const offerStateChangeEventData: OfferStateChangeEvent = {
            quotes: event.data.quotes
          }

          if (this.config.unique_offer_id) {
            offerStateChangeEventData.unique_offer_id = this.config.unique_offer_id;
          }

          this.wrapper.dispatchEvent(new CustomEvent<OfferStateChangeEvent>("offer-state-change", {
            bubbles: true,
            detail: offerStateChangeEventData
          }));

          if (this.onOfferStateChange) {
            this.onOfferStateChange(offerStateChangeEventData);
          }

          this.handleStateChange(offerStateChangeEventData as StateChangeEvent);
        }

        if (isOfferReadyMessage(event.data)) {
          const offerReadyEventData: OfferReadyEvent = {
            offersAvailable: event.data.offersAvailable
          }

          this.wrapper.dispatchEvent(new CustomEvent<OfferReadyEvent>("offer-ready", {
            bubbles: true,
            detail: offerReadyEventData
          }));

          if (this.onOfferReady) {
            this.onOfferReady(event.data.offersAvailable);
          }
        }

        if (isEventInsuranceRequirementsStateChangeEvent(event.data)) {
          if (!event.data.event) return;

          this.wrapper.dispatchEvent(new CustomEvent<EventInsuranceRequirementsStateChangeEvent>("event-insurance-requirements-state-change", {
            bubbles: true,
            detail: event.data.event
          }));

          if (this.onEventInsuranceRequirementsStateChange) {
            this.onEventInsuranceRequirementsStateChange(event.data.event);
          }

          this.handleStateChange(event.data.event as StateChangeEvent);
        }

        if (isEventExhibitorLiabilityStateChangeMessage(event.data)) {
          if (!event.data.event_exhibitor) return;

          this.wrapper.dispatchEvent(new CustomEvent<EventExhibitorLiabilityStateChangeEvent>("event-exhibitor-liability-state-change", {
            bubbles: true,
            detail: event.data.event_exhibitor
          }));

          if (this.onEventExhibitorLiabilityStateChange) {
            this.onEventExhibitorLiabilityStateChange(event.data.event_exhibitor);
          }

          this.handleStateChange(event.data.event_exhibitor as StateChangeEvent);
        }

        if (isStateChangeMessage(event.data)) {
          const { data } = event.data;
          let detail: unknown = data;

          if ('event_exhibitor' in data) {
            detail = data.event_exhibitor;
          } else if ('event' in data) {
            detail = data.event;
          }

          this.handleStateChange(detail as StateChangeEvent);
        }

        if (isErrorMessage(event.data)) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { eventType, ...errorEvent } = event.data;
          this.handleError(errorEvent);
        }
      }
    }, { signal: this.eventAbort.signal });

    this.element.src = this.frameSource;
    this.element.setAttribute("style", "border: 0; width: 100%; overflow: hidden;");
    this.element.setAttribute("scrolling", "no");

    // Set the initial height for the iframe to 0. The height will be overridden by the resize handler.
    this.element.height = "0px";
    this.wrapper.appendChild(this.element);
  }

  /* @internal */
  private handleStateChange(event: StateChangeEvent) {
    this.wrapper.dispatchEvent(new CustomEvent<StateChangeEvent>("state-change", {
      bubbles: true,
      detail: event
    }));

    if (this.onChange) {
      this.onChange(event);
    }
  }

  /* @internal */
  private handleError(errorEvent: ErrorEvent) {
    this.wrapper.dispatchEvent(new CustomEvent<ErrorEvent>("error", {
      bubbles: true,
      detail: errorEvent
    }));

    if (this.onError) {
      this.onError(errorEvent);
    }
  }

  /**
   * Validates the offer to ensure its either accepted or declined. Validation errors are displayed to the user.
   * @returns Boolean - Whether the offer is valid or not.
   */
  async validate(): Promise<boolean> {
    if (!this.element) {
      const error = new Error("Vertical Insure element has not been initialized.");
      this.handleError({
        error: "Vertical Insure element has not been initialized.",
        type: 'client',
        source: 'validate'
      });
      throw error;
    }

    const response = await this.frameController?.postMessageWithResponseListener(EventType.validate);
    return response.is_valid;
  }

  /**
   * Tokenizes the payment method for the offer if applicable.
   * @throws Error - If the offer validation fails.
   * @returns Object - The tokenized payment method.
   */
  async tokenizePaymentMethod(): Promise<Omit<TokenizePaymentMessage, 'eventType'>> {
    if (!this.element) {
      const error = new Error("Vertical Insure element has not been initialized.");
      this.handleError({
        error: "Vertical Insure element has not been initialized.",
        type: 'client',
        source: 'tokenizePaymentMethod'
      });
      throw error;
    }

    if (await this.validate()) {
      return this.frameController?.postMessageWithResponseListener(EventType.tokenize_payment);
    } else {
      throw new Error("Vertical Insure offer validation failed");
    }
  }

  update(config: ProductConfig | ComponentConfig): void {
    if (!this.element) {
      const error = new Error("Vertical Insure element has not been initialized.");
      this.handleError({
        error: "Vertical Insure element has not been initialized.",
        type: 'client',
        source: 'update'
      });
      throw error;
    }
    
    // Determine if this is a ProductConfig or ComponentConfig
    // ComponentConfig has specific properties like event_config, event_exhibitor_config, etc.
    const isProductConfig = (config: ProductConfig | ComponentConfig): config is ProductConfig => {
      // ProductConfig is a record where all values are arrays of QuoteRequest
      // ComponentConfig has specific optional properties
      return typeof config === 'object' && 
             config !== null &&
             !('event_config' in config) &&
             !('event_exhibitor_config' in config) &&
             !('event_cancellation' in config) &&
             !('room_block_protection' in config) &&
             Object.values(config).every(value => Array.isArray(value));
    };

    const updateMessage = isProductConfig(config)
      ? { eventType: EventType.update, product_config: config }
      : { eventType: EventType.update, component_config: config };

    this.frameController?.postMessageWithResponseListener(EventType.update, updateMessage);
  }

  destroy(): void {
    try { this.eventAbort.abort(); } catch { /* empty */ }
    try { this.element.src = "about:blank"; } catch { /* empty */ }
    if (this.element?.isConnected) {
      this.element.remove();
    }

    this.onOfferReady = undefined;
    this.onOfferStateChange = undefined;
    this.onEventInsuranceRequirementsStateChange = undefined;
    this.onEventExhibitorLiabilityStateChange = undefined;
    this.onError = undefined;
  }

  /**
   * This method generates a thumbmark (browser fingerprint) using ThumbmarkJS and returns it.
   * The thumbmark can be passed to the backend for offer tracking and conversion analytics.
   * @param client_id - Client ID part of your API key.
   * @returns Promise<string> - offer id
   */
  static async trackOffer(client_id: string): Promise<string> {
    const tm = new Thumbmark();
    const result = await tm.get();
    const thumbmark = result.thumbmark;
    const response = await fetch(`${VERTICAL_INSURE_API_URL}/v1/offer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-client-id': client_id
      },
      body: JSON.stringify({ thumbmark })
    });
    if (!response.ok) {
      throw new Error(`Failed to create or get offer: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return data.id;
  }
}
